import { getSignedImageUrls } from '@/lib/storage-urls'

/**
 * ALL logos live inside the 'fotos-obras' bucket.
 * 'company-logos', 'logos', 'cliente-logos' are FOLDER PREFIXES inside it,
 * NOT separate physical buckets.
 */
const LOGO_BUCKET = 'fotos-obras'

/**
 * Extract the storage path from a full Supabase storage URL.
 * Handles /public/, /sign/, and /object/ URL formats.
 * Returns the raw path inside the bucket (e.g. "uuid/file.png").
 *
 * Strips bucket name if it appears duplicated as the first path segment,
 * e.g. URLs stored as /public/company-logos/company-logos/uuid/logo.png
 * → bucket="company-logos", path="uuid/logo.png"
 */
function extractPathFromUrl(url: string): { bucket: string; path: string } | null {
  try {
    const urlObj = new URL(url)
    // Match: /storage/v1/object/[public|sign|]/BUCKET/PATH
    const m = urlObj.pathname.match(
      /\/storage\/v1\/object\/(?:public\/|sign\/)?([^/]+)\/(.+)/
    )
    if (!m) return null
    const bucket = m[1]
    let path = decodeURIComponent(m[2]).replace(/\?.*$/, '') // strip query params
    // Strip bucket name if duplicated as the first path segment
    if (path.startsWith(bucket + '/')) {
      path = path.slice(bucket.length + 1)
    }
    return { bucket, path }
  } catch {
    return null
  }
}

/**
 * Resolve a logo_url (relative path or full Supabase URL) into a signed URL.
 *
 * Uses the `get-signed-urls` edge function (service role) to bypass storage RLS.
 *
 * Accepts:
 *  - Relative paths: "company-logos/uuid/file.png", "logos/uuid/logo.png"
 *  - Full Supabase URLs: "https://.../object/public/company-logos/..."
 *  - External URLs (returned as-is)
 *
 * Returns null if resolution fails — never throws.
 */
export async function resolveLogoUrl(logoUrl: string | null | undefined): Promise<string | null> {
  if (!logoUrl || !logoUrl.trim()) return null

  const url = logoUrl.trim()

  // External URL (not our Supabase instance) — use directly
  if (url.startsWith('http') && !url.includes('supabase.co/storage')) {
    return url
  }

  // Determine initial bucket and path
  let bucket: string
  let path: string

  if (url.startsWith('http')) {
    const extracted = extractPathFromUrl(url)
    if (!extracted) return null
    bucket = extracted.bucket
    path = extracted.path
  } else {
    // Relative path — always in fotos-obras bucket
    bucket = LOGO_BUCKET
    path = url.replace(/^\/+/, '')
  }

  // Build list of (bucket, path) candidates to try, in priority order.
  // All fotos-obras attempts are batched into a single edge function call.
  const fotosObrasPaths: string[] = []

  if (bucket === LOGO_BUCKET) {
    // Path is already relative to fotos-obras
    fotosObrasPaths.push(path)
  } else {
    // bucket is something like 'company-logos' — file may live in fotos-obras
    // under a folder with the same name as the bucket
    fotosObrasPaths.push(`${bucket}/${path}`)  // e.g. company-logos/uuid/logo.png
    fotosObrasPaths.push(path)                  // fallback: uuid/logo.png directly
  }

  try {
    const urlMap = await getSignedImageUrls(LOGO_BUCKET, fotosObrasPaths, 3600)
    for (const p of fotosObrasPaths) {
      if (urlMap[p]) return urlMap[p]
    }
  } catch (err) {
    console.error('[resolve-logo] getSignedImageUrls failed', err)
  }

  return null
}
